import { useAppServices } from "hook/services";
import { CheckCircle, Eye, X } from "lucide-react";
import { useEffect, useMemo, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useUserInfo } from "context/user";

export default function SubAccountsData() {
  const AppService = useAppServices();
  const navigate = useNavigate();
  const { user } = useUserInfo();
  const [subAccounts, setSubAccounts] = useState<any>([]);
  const [loading, setLoading] = useState<boolean>(true);
  
  // Server-side pagination and filtering states
  const [pagination, setPagination] = useState({
    page: 1, // Backend uses 1-based page indexing
    limit: 10,
    total: 0,
    pages: 0,
    sortField: "createdAt",
    sortOrder: "desc"
  });
  const [searchTerm, setSearchTerm] = useState<string>("");
  
  // Prevent double calls in development
  const hasFetched = useRef(false);

  const buildQueryString = () => {
    const params = new URLSearchParams();
    
    // Required company filter
    if (user?.companyId) {
      params.append("companyId", user.companyId);
    }
    
    // Pagination parameters
    params.append("page", pagination.page.toString());
    params.append("limit", pagination.limit.toString());
    
    // Search parameter
    if (searchTerm) {
      params.append("search", searchTerm);
    }
    
    // Sorting parameters
    if (pagination.sortField && pagination.sortOrder) {
      params.append("sortBy", pagination.sortField);
      params.append("sortOrder", pagination.sortOrder);
    }
    
    return params.toString();
  };

  const getSubAccounts = async () => {
    setLoading(true);
    try {
      const query = buildQueryString();
      const { response, error } = await AppService.sub_account.filter({
        query: query,
      });
      
      if (response && response.data) {
        setSubAccounts(response.data);
        
        // Update pagination info from API response
        if (response.pagination) {
          setPagination(prev => ({
            ...prev,
            total: response.pagination.total,
            pages: response.pagination.pages
          }));
        }
      } else {
        setSubAccounts([]);
        setPagination(prev => ({
          ...prev,
          total: 0,
          pages: 0
        }));
      }
    } catch (error) {
      console.error("Error fetching sub accounts:", error);
      setSubAccounts([]);
    } finally {
      setLoading(false);
    }
  };

  // Load data when dependencies change
  useEffect(() => {
    // Prevent double calls in development (Strict Mode)
    if (hasFetched.current) {
      getSubAccounts();
    } else {
      hasFetched.current = true;
    }
  }, [pagination.page, pagination.limit, searchTerm, pagination.sortField, pagination.sortOrder]);

  const handleView = (accountId: string) => {
    navigate(`/location/${accountId}/phone_bridge/dashboard`);
  };

  const Status = ({ item }: any) => {
    switch (item) {
      case "active":
        return (
          <span className="px-2.5 py-0.5 text-xs font-medium rounded border bg-green-100 border-transparent text-green-500 dark:bg-green-500/20 dark:border-transparent inline-flex items-center status">
            <CheckCircle className="size-3 mr-1.5" />
            {item}
          </span>
        );
      case "inactive":
        return (
          <span className="px-2.5 py-0.5 inline-flex items-center text-xs font-medium rounded border bg-red-100 border-transparent text-red-500 dark:bg-red-500/20 dark:border-transparent status">
            <X className="size-3 mr-1.5" />
            {item}
          </span>
        );
      default:
        return (
          <span className="px-2.5 py-0.5 text-xs font-medium rounded border bg-green-100 border-transparent text-green-500 dark:bg-green-500/20 dark:border-transparent inline-flex items-center status">
            <CheckCircle className="size-3 mr-1.5" />
            {item}
          </span>
        );
    }
  };

  // Handle search
  const handleSearch = (value: string) => {
    setSearchTerm(value);
    setPagination(prev => ({ ...prev, page: 1 })); // Reset to first page on search
  };

  // Handle page change (1-based)
  const handlePageChange = (page: number) => {
    setPagination(prev => ({ ...prev, page }));
  };

  // Handle page size change
  const handlePageSizeChange = (limit: number) => {
    setPagination(prev => ({ ...prev, limit, page: 1 }));
  };

  // Handle sorting
  const handleSort = (field: string, direction: string) => {
    setPagination(prev => ({ 
      ...prev, 
      sortField: field, 
      sortOrder: direction,
      page: 1 // Reset to first page when sorting
    }));
  };

  const columns = useMemo(
    () => [
      {
        header: "Name",
        accessorKey: "name",
        enableColumnFilter: false,
        enableSorting: true,
        cell: (cell: any) => (
          <div className="flex items-center gap-2">
            <div className="flex items-center justify-center size-10 font-medium rounded-full shrink-0 bg-slate-200 text-slate-800 dark:text-zink-50 dark:bg-zink-600">
              {cell.row.original.img ? (
                <img
                  src={cell.row.original.img}
                  alt=""
                  className="h-10 rounded-full"
                />
              ) : (
                cell
                  .getValue()
                  .split(" ")
                  .map((word: any) => word.charAt(0))
                  .join("")
              )}
            </div>
            <div className="grow">
              <h6 className="mb-1">
                <Link to="#!" className="name">
                  {cell.getValue()}
                </Link>
              </h6>
              <p className="text-slate-500 dark:text-zink-200">
                {cell.row.original.designation}
              </p>
            </div>
          </div>
        ),
      },
      {
        header: "Email",
        accessorKey: "email",
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: "Phone Number",
        accessorKey: "phone",
      },
      {
        header: "Status",
        accessorKey: "status",
        cell: (cell: any) => <Status item={cell.getValue()} />,
      },
      {
        header: "Action",
        cell: (cell: any) => (
          <>
            <span
              className="bg-custom-500 rounded-md text-white p-2 cursor-pointer"
              onClick={() => handleView(cell.row.original?._id)}
            >
              <Eye className="inline-block size-4 " />
            </span>
          </>
        ),
      },
    ],
    []
  );

  return {
    loading: loading,
    subAccounts: subAccounts,
    columns: columns,
    pagination: pagination,
    searchTerm: searchTerm,
    handleSearch: handleSearch,
    handlePageChange: handlePageChange,
    handlePageSizeChange: handlePageSizeChange,
    handleSort: handleSort,
  };
}